import React, { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    enquiryType: 'General Enquiry',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contacts`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: `[${formData.enquiryType}] ${formData.message}`
      });
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        enquiryType: 'General Enquiry',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title="Contact Us"
        description="Get in touch with I DO Bridal Magazine Malta. Share your wedding story, advertise with us, or simply say hello."
      />
      {/* Hero Section with Image */}
      <section className="relative">
        {/* Hero Image Banner */}
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=1600&h=800&fit=crop"
            alt="Contact I DO Magazine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FAF9F7]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-white/90 text-xs tracking-[0.3em] uppercase mb-4">
                GET IN TOUCH
              </p>
              <h1 
                className="text-4xl md:text-6xl text-white mb-4 tracking-tight drop-shadow-lg"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                We'd Love to Hear From You
              </h1>
            </div>
          </div>
        </div>
        
        {/* Intro Text */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-12">
          <div className="w-20 h-0.5 mx-auto mb-8" style={{ backgroundColor: '#DE92A9' }}></div>
          <p className="text-lg leading-relaxed" style={{ color: '#757B7F' }}>
            Whether you're a couple hoping to share your wedding story, a wedding professional interested in working with us, or a reader with a question, the I DO team is here to help.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 md:p-12 rounded-lg shadow-md">
            <h2 
              className="text-3xl text-[#2C2C2C] mb-2 text-center"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Get In Touch
            </h2>
            <p className="text-center text-[#6B4E37] mb-8">
              For general enquiries, submissions, or collaborations
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    First Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Last Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    required
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Enquiry Type *
                </label>
                <Select value={formData.enquiryType} onValueChange={(value) => setFormData({...formData, enquiryType: value})}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                    <SelectItem value="Wedding Submission">Wedding Submission</SelectItem>
                    <SelectItem value="Vendor Partnership">Vendor Partnership</SelectItem>
                    <SelectItem value="Advertising">Advertising</SelectItem>
                    <SelectItem value="Editorial">Editorial</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Message *
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                  rows={6}
                  className="w-full"
                  placeholder="Tell us about your enquiry..."
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#DE92A9] hover:bg-[#C9758F] text-white py-6 text-sm tracking-wider"
              >
                {loading ? 'SENDING...' : 'SUBMIT'}
                <Send className="ml-2" size={18} />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl text-[#2C2C2C] mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              The I DO Team
            </h2>
            <div className="w-20 h-0.5 bg-[#DE92A9] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {/* Editor */}
            <div className="bg-[#FAF9F7] p-8 rounded-lg text-center">
              <div className="w-20 h-20 bg-[#DE92A9] rounded-full mx-auto mb-4 flex items-center justify-center">
                <Mail className="text-white" size={32} />
              </div>
              <h3 className="text-xl text-[#2C2C2C] mb-2 font-semibold">Editor</h3>
              <p className="text-[#6B4E37] mb-3">Jadine Arnold</p>
              <a 
                href="mailto:idobridalmagazinemalta@gmail.com" 
                className="text-[#DE92A9] hover:text-[#C9758F] text-sm break-all"
              >
                idobridalmagazinemalta@gmail.com
              </a>
              <p className="text-[#8B7D6B] text-xs mt-3">
                Editorial enquiries, wedding stories & features
              </p>
            </div>

            {/* Sales */}
            <div className="bg-[#FAF9F7] p-8 rounded-lg text-center">
              <div className="w-20 h-20 bg-[#DE92A9] rounded-full mx-auto mb-4 flex items-center justify-center">
                <Mail className="text-white" size={32} />
              </div>
              <h3 className="text-xl text-[#2C2C2C] mb-2 font-semibold">Sales & Partnerships</h3>
              <p className="text-[#6B4E37] mb-3">Tanya Sawyer</p>
              <a 
                href="mailto:tanyasawyer@independent.com.mt" 
                className="text-[#DE92A9] hover:text-[#C9758F] text-sm break-all"
              >
                tanyasawyer@independent.com.mt
              </a>
              <p className="text-[#8B7D6B] text-xs mt-3">
                Advertising, partnerships & featured opportunities
              </p>
            </div>

            {/* Design */}
            <div className="bg-[#FAF9F7] p-8 rounded-lg text-center">
              <div className="w-20 h-20 bg-[#DE92A9] rounded-full mx-auto mb-4 flex items-center justify-center">
                <Mail className="text-white" size={32} />
              </div>
              <h3 className="text-xl text-[#2C2C2C] mb-2 font-semibold">Design</h3>
              <p className="text-[#6B4E37] mb-3">Christine Spiteri</p>
              <a 
                href="mailto:cmifsud@independent.com.mt" 
                className="text-[#DE92A9] hover:text-[#C9758F] text-sm break-all"
              >
                cmifsud@independent.com.mt
              </a>
              <p className="text-[#8B7D6B] text-xs mt-3">
                Creative direction & visual identity
              </p>
            </div>

            {/* Graphic Designer */}
            <div className="bg-[#FAF9F7] p-8 rounded-lg text-center">
              <div className="w-20 h-20 bg-[#DE92A9] rounded-full mx-auto mb-4 flex items-center justify-center">
                <Mail className="text-white" size={32} />
              </div>
              <h3 className="text-xl text-[#2C2C2C] mb-2 font-semibold">Graphic Designer</h3>
              <p className="text-[#6B4E37] mb-3">Conrad Bondin</p>
              <a 
                href="mailto:cbondin@independent.com.mt" 
                className="text-[#DE92A9] hover:text-[#C9758F] text-sm break-all"
              >
                cbondin@independent.com.mt
              </a>
              <p className="text-[#8B7D6B] text-xs mt-3">
                Graphic design & visual layouts
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;